import { graphql } from 'react-relay';

import { EditorCardProvider } from '@atlaskit/editor-card-provider';
import { isSSR } from '@atlaskit/editor-common/core-utils';
import { EmojiResource } from '@atlaskit/emoji/resource';
import { prefetchNodeDataProvidersData } from '@atlaskit/node-data-provider';
import { CardClient } from '@atlaskit/link-provider';

import { getSessionData as uninstrumentedGetSessionData } from '@confluence/session-data/entry-points/SessionData.ts';
import { type SessionDataType } from '@confluence/session-data/entry-points/SessionDataTypes.ts';
import { createEmojiProviderParameters } from '@confluence/fabric-emoji-support';
import { getEmojiNodeDataProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider.ts';
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { createCookie } from '@confluence/content-smartlinks/entry-points/createCookie.ts';
import { expVal } from '@confluence/feature-experiments';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';
import { fg } from '@confluence/feature-gating';

import type {
	preloadEditorNodeDataProvidersDataQuery$data,
	preloadEditorNodeDataProvidersDataQuery$variables,
} from './__generated__/preloadEditorNodeDataProvidersDataQuery.graphql.ts';
import {
	NODE_DATA_PROVIDERS_MAX_NODES_TO_PREFETCH,
	NODE_DATA_PROVIDERS_MAX_NODES_TO_VISIT,
} from './utils/nodeDataProvidersConstants.ts';
import { addToSSREditorNodeDataProvidersStore } from './utils/ssrDataStore.ts';
import { preloadEditorSyncedBlocksData } from './preloadEditorSyncedBlocksData.ts';
import { isValidJSONDocNode } from './utils/isValidJSONDocNode.ts';
import { isJSONDocNode } from '@atlaskit/editor-json-transformer';

const MEASURE_SEGMENT = 'ssr-app/prepare/preloadQuery/editor/nodeDataProvidersData';
const DEFAULT_TIMEOUT = 300;

const preloadEditorNodeDataProvidersDataQuery = graphql`
	query preloadEditorNodeDataProvidersDataQuery($contentId: ID, $status: [String]) {
		singleContent(id: $contentId, status: $status) {
			id
			body {
				atlas_doc_format {
					value
				}
			}
		}
	}
`;

export async function preloadEditorNodeDataProvidersData(
	contentId: string | null,
	cloudId?: string,
	isBlog?: boolean,
): Promise<void> {
	if (!contentId) {
		return;
	}

	if (!isSSR()) {
		// If we are not in SSR, we don't need to preload node data providers data.
		return;
	}

	await SSRMeasures.run(MEASURE_SEGMENT, async () => {
		const fetchAdfStart = performance.now();
		const docJsonString = await fetchAdf(contentId);

		const fetchAdfDuration = performance.now() - fetchAdfStart;

		if (!docJsonString) {
			return;
		}

		const doc = JSON.parse(docJsonString);

		// Validate that the parsed document is a valid JSONDocNode
		if (!isValidJSONDocNode(doc) && fg('platform_synced_block_ssr_v2')) {
			return;
		}

		const providers: Parameters<typeof prefetchNodeDataProvidersData>[0]['providers'] = [];

		let syncedBlocksPromise: Promise<void> | undefined;
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		if (expVal('platform_synced_block', 'isEnabled', false) && fg('platform_synced_block_ssr_v2')) {
			if (fg('platform_synced_block_patch_3')) {
				if (isJSONDocNode(doc)) {
					syncedBlocksPromise = preloadEditorSyncedBlocksData(contentId, cloudId, isBlog, doc);
				}
			} else {
				const content = doc.content || [];
				const hasSyncedBlock = content.some((node) => {
					if (!node || !node.type || typeof node.type !== 'string') {
						return false;
					}
					return node.type === 'syncBlock';
				});

				if (hasSyncedBlock) {
					// initiate preload but don't await it yet
					syncedBlocksPromise = preloadEditorSyncedBlocksData(contentId, cloudId, isBlog);
				}
			}
		}

		providers.push({
			provider: ssrEditorCardProvider(),
			maxNodesToPrefetch: NODE_DATA_PROVIDERS_MAX_NODES_TO_PREFETCH.smartCard,
		});

		const sessionData = await getSessionData();
		const emojiNodeDataProvider = ssrEmojiNodeDataProvider(sessionData);
		if (emojiNodeDataProvider) {
			providers.push({
				provider: emojiNodeDataProvider,
				maxNodesToPrefetch: NODE_DATA_PROVIDERS_MAX_NODES_TO_PREFETCH.emoji,
			});
		}

		providers.forEach(({ provider }) => {
			SSRMeasures.markMeasureStart(measureProviderSegment(provider.name));
		});

		const editorNodeDataProvidersData = await prefetchNodeDataProvidersData({
			doc,
			providers,
			timeout: DEFAULT_TIMEOUT - fetchAdfDuration,
			maxNodesToVisit: NODE_DATA_PROVIDERS_MAX_NODES_TO_VISIT,
		});

		Object.entries(editorNodeDataProvidersData).forEach(([providerName, { duration }]) => {
			SSRMeasures.setMeasureDuration(measureProviderSegment(providerName), duration);
		});

		if (
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			expVal('platform_synced_block', 'isEnabled', false)
		) {
			const nodeDataToStore = Object.fromEntries(
				Object.entries(editorNodeDataProvidersData).map(([providerName, { data }]) => [
					providerName,
					data,
				]),
			);
			addToSSREditorNodeDataProvidersStore(nodeDataToStore);
		} else {
			window['__SSR_EDITOR_NODE_DATA_PROVIDERS_DATA__'] = Object.fromEntries(
				Object.entries(editorNodeDataProvidersData).map(([providerName, { data }]) => [
					providerName,
					data,
				]),
			);
		}

		// Ensure synced blocks data is fully resolved before returning
		if (syncedBlocksPromise) {
			await syncedBlocksPromise;
		}
	});
}

function fetchAdf(contentId: string | undefined | null): Promise<string | undefined> {
	return SSRMeasures.run(`${MEASURE_SEGMENT}/fetch:AdfDocQuery`, async () => {
		try {
			const result = await query<
				preloadEditorNodeDataProvidersDataQuery$data,
				preloadEditorNodeDataProvidersDataQuery$variables
			>({
				query: preloadEditorNodeDataProvidersDataQuery,
				variables: {
					contentId,
					status: ['draft'],
				},
				fetchPolicy: 'network-only',
			});

			if (result.hasErrors) {
				return undefined;
			}

			return result.data?.singleContent?.body?.atlas_doc_format?.value || undefined;
		} catch {
			return undefined;
		}
	});
}

function getSessionData(): Promise<SessionDataType> {
	return SSRMeasures.run(`${MEASURE_SEGMENT}/getSessionData`, async () => {
		return uninstrumentedGetSessionData();
	});
}

function ssrEmojiNodeDataProvider(sessionData: SessionDataType) {
	const { userId, cloudId, isLicensed, isLoggedIn } = sessionData;

	// We need a special emoji parameters that supports SSR load optimization here.
	const emojiResourceParams = createEmojiProviderParameters({
		cloudId,
		userId,
		isLicensed,
		isLoggedIn,
	});

	return getEmojiNodeDataProvider(EmojiResource, {
		...emojiResourceParams,
		allowAnonymousAccess: true,
	});
}

export function ssrEditorCardProvider() {
	const { host, cloudId, userContext, customURLs } = getSSRRenderInputs();

	// This URL will be something like `https://object-resolver.staging.atl-paas.net/resolve/batch`.
	// We need only protocol and host to pass it to the EditorCardProvider,
	// because path will be added by `CardClient`.
	const objectResolverURL =
		customURLs?.['object-resolver-service']?.match(/^(https?:\/\/[^/]+)/)?.[0];

	// We need to define a custom CardClient for SSR, because the default one works through public API gateway.
	const ssrCardClient = new CardClient('custom', objectResolverURL);
	// Headers from `fetchORSForSSR` from confluence/next/packages/content-smartlinks/src/index.ts
	ssrCardClient.setHeaders({
		Cookie: createCookie(userContext),
		Origin: host.startsWith('http') ? host : `https://${host}`,
		CloudId: cloudId,
	});

	return new EditorCardProvider(
		'custom',
		objectResolverURL,
		'CONFLUENCE',
		undefined,
		ssrCardClient,
	);
}

function measureProviderSegment(providerName: string) {
	return `${MEASURE_SEGMENT}/${providerName}`;
}
